import find from 'lodash/find';
import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
import { createSlug, stringify } from './urlHelpers';
import { LANGUAGE_CODES, LOCALE_CODE, LOCALE_TO_LANG } from '../constants/locales';
import { createLocationFromAddressParts } from './listingHelpers';

const findRouteByName = (nameToFind, routes) => find(routes, route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;

  const matches = matchPathname(pathname, routes);
  const isListingRoute = matches.length === 1 && matches[0].route.name === 'ListingPage';

  const cleanedPathName = pathname.replace(/\/$/, '');

  if (isListingRoute) {
    const parts = cleanedPathName.split('/');

    const canonicalListingPathname = `/${parts[1]}/${parts[2]}/${parts[3]}/${parts[4]}`;

    return pathOnly ? canonicalListingPathname : `${canonicalListingPathname}${search}${hash}`;
  }

  return pathOnly ? cleanedPathName : `${cleanedPathName}${search}${hash}`;
};

export const alternateHreflangs = (
  canonicalPath,
  stPagesData,
  listingMultiLangTitles,
  listingMultiLangActivity,
  listingMultiLangAddress,
  intl,
  listingMultiLangTypes,
  seoUrlsInAllLang
) => {
  const parts = canonicalPath.split('/');

  // If custom sharetribe page
  if (parts[2] === 'p') {
    const pageName = parts[3];

    const stPageGroup = stPagesData.find(pageGroup => pageGroup.includes(pageName));

    if (stPageGroup && stPageGroup.length > 1) {
      return stPageGroup
        .filter(page => page !== pageName)
        .map(altPage => {
          const altLocalePlain = altPage.slice(0, 2);

          const altLocale =
            LANGUAGE_CODES.find(localeCode => localeCode.startsWith(altLocalePlain)) ||
            LOCALE_CODE.EN_US;

          return { path: `/${altLocale}/p/${altPage}`, locale: altLocale };
        });
    }
  }

  if (seoUrlsInAllLang) {
    // Check if we actually have valid localized URLs
    const hasValidLocalizedUrls = Object.keys(seoUrlsInAllLang).length > 0 && 
      Object.values(seoUrlsInAllLang).some(url => url && url.trim() !== '');
    
    if (!hasValidLocalizedUrls) {
      // Return empty array instead of generating incorrect URLs
      return [];
    }
    
    const result = LANGUAGE_CODES.map(locale => {
      // Use the localized URL path directly from the mappings
      const localizedUrlPath = seoUrlsInAllLang[locale];
      
      if (localizedUrlPath) {
        // Ensure localizedUrlPath doesn't start with '/' and add locale prefix
        const cleanPath = localizedUrlPath.startsWith('/') ? localizedUrlPath.slice(1) : localizedUrlPath;
        const path = `/${locale}/${cleanPath}`;
        return {
          path,
          locale,
        };
      }
      
      // If no localized URL for this locale, skip it instead of generating a fallback
      return null;
    }).filter(Boolean); // Remove null entries
    
    return result;
  }

  return LANGUAGE_CODES.map(locale => {
    const path = localizeListingPagePath(
      canonicalPath,
      locale,
      {
        // These will only be available for ListingPage
        slug: listingMultiLangTitles?.[LOCALE_TO_LANG[locale]],
        location: createLocationFromAddressParts(listingMultiLangAddress?.[LOCALE_TO_LANG[locale]]),
        activity: listingMultiLangActivity?.[LOCALE_TO_LANG[locale]],
        type: listingMultiLangTypes?.[LOCALE_TO_LANG[locale]],
      },
      true,
      intl
    );

    return {
      path,
      locale,
    };
  });
};

export const localizeListingPagePath = (pathname, locale, listingParams, isCanonicalPath, intl) => {
  const { slug, location, activity, type } = listingParams;

  const parts = pathname.split('/');
  const originalParts = [...parts];
  parts[1] = locale;

  // Check if this is a variant URL (draft, pending-approval, closed)
  // Variant URL structure: /:locale/l/:slug/:id/:variant
  if (originalParts.length >= 6 && originalParts[2] === 'l') {
    const variant = originalParts[5];
    if (variant === 'draft' || variant === 'pending-approval' || variant === 'closed') {
      // For variant URLs, update the slug with the localized version but keep the structure
      const localizedSlug = slug || originalParts[3];
      return `/${locale}/l/${localizedSlug}/${originalParts[4]}/${variant}`;
    }
  }

  // Regular listing URL structure: /:locale/:type/:slug/:id
  if (type) {
    parts[2] = type;
  }

  // if (intl && parts[2]) {
  //   try {
  //     parts[2] = createSlug(intl.formatMessage({ id: `ListingTypes.${createSlug(type)}` }));
  //   } catch (error) {
  //     console.log('Translation not found for:', parts[2]);
  //   }
  // }

  if (location && activity && slug) {
    parts[3] = createSlug(slug);
    // parts[4] = createSlug(activity);
    // parts[isCanonicalPath ? 5 : 6] = createSlug(slug);
  }

  return parts.join('/');
};

export const localizeSeoPagePath = (pathname, locale, localizedUrlMappings) => {
  if (!localizedUrlMappings || typeof localizedUrlMappings !== 'object') {
    // Fallback: just change the locale part of the URL
    const parts = pathname.split('/');
    parts[1] = locale;
    return parts.join('/');
  }

  // Get the localized URL path for the target locale
  const localizedUrlPath = localizedUrlMappings[locale];
  
  if (localizedUrlPath) {
    // Build the complete localized path: /locale/localizedUrlPath
    return `/${locale}/${localizedUrlPath}`;
  }

  // Fallback: just change the locale part if no mapping found
  const parts = pathname.split('/');
  parts[1] = locale;
  return parts.join('/');
};
